import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FieldArray, Form } from 'formik';
import { Switch } from '@rsos/base-components';
import Loader from '@rsos/components/capstone/Loader';
import { CheckBox } from '@rsos/components/capstone/base';
import { fetchAlertTypes } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { patchOrgAttributes } from '@rsos/sinatra';
import { notifyError } from '@rsos/utils/toastUtils';
import { mockAlertToastsSettings } from '../../mock';
import styles from '../../settings.module.css';
import Disclaimer from './Disclaimer';
import EmptyState from './EmptyState';
import FetchErrorComponent from './FetchErrorComponent';
import { trackToggleAlertsToastsOnOff } from './alertToastsNotificationsTrackings';
import {
  getIsCheckboxDisabled,
  filterAlertTypesByCapabilities,
  UPDATE_SETTINGS_ERROR,
  getCheckboxValues,
  useMigratedAlerts,
} from './utils';

const DigitalAlertsBannerSettings = () => {
  const dispatch = useDispatch();

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const currentOrg = useSelector(state => state.sinatra.user.currentOrg);
  const applicationName = useSelector(
    state => state.sinatra.user.currentRole.application,
  );

  const orgID = useMemo(() => currentOrg?.id, [currentOrg]);
  const orgName = useMemo(() => currentOrg?.name, [currentOrg]);

  /*
   * mockAlertToastsSettings contain all possible alert types with enabled as true
   * we don't know until the /alerts-type call is made which alert types the used has enabled
   * in andromeda. before saving the new settings, we diff mock with the enabled alert types
   */
  const resolvedOrgAlertToastsSettings = useSelector(
    state =>
      state.sinatra.user.profile?.organizations?.find(o => o.id === orgID)
        ?.attributes[applicationName]?.alert_toasts || mockAlertToastsSettings,
  );

  const alertTypes = useMigratedAlerts(resolvedOrgAlertToastsSettings);

  const [localAlertToastToggle, setLocalAlertToastToggle] = useState(
    Object.values(alertTypes).some(v => v.enabled),
  );

  const availableAlertTypes = useSelector(
    state => state?.incidents?.alertTypes,
  );

  const isFetchingAlertTypes = useSelector(
    state => state.incidents?.loading?.alertTypes,
  );
  const fetchAlertTypesError = useSelector(
    state => state.incidents?.error?.alertTypes,
  );
  const handleFetchAlertTypes = useCallback(() => {
    if (orgName) {
      dispatch(fetchAlertTypes(orgName));
    }
  }, [orgName, dispatch]);

  // fetch alert types enabled in andromeda on load
  useEffect(() => {
    handleFetchAlertTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAlertToastsSettings = setting => {
    const updatedSettings = {
      alert_toasts: {
        alert_types: filterAlertTypesByCapabilities(
          setting,
          availableAlertTypes,
        ),
      },
    };

    dispatch(patchOrgAttributes(orgID, applicationName, updatedSettings)).catch(
      () => {
        notifyError(UPDATE_SETTINGS_ERROR);
      },
    );
  };
  // toggle here acts as select/deselect all for checkboxes
  const toggleAlertToasts = setValues => {
    let localAlertTypesCopy = alertTypes;

    Object.values(localAlertTypesCopy).forEach(item => {
      localAlertTypesCopy[item.name] = {
        ...item,
        enabled: localAlertToastToggle
          ? getIsCheckboxDisabled(item.name)
            ? item.enabled
            : false
          : true,
      };
    });

    updateAlertToastsSettings(localAlertTypesCopy);
    setValues({
      usersAlertTypes: getCheckboxValues(localAlertTypesCopy),
    });
    trackToggleAlertsToastsOnOff(irpVersion, !localAlertToastToggle);
    setLocalAlertToastToggle(!localAlertToastToggle);
  };

  const handleCheckbox = (event, value, checkboxValues, arrayHelpers) => {
    let updatedAlertTypes;

    if (event.target.checked) {
      // checking off a checkbox
      arrayHelpers.push(value);

      updatedAlertTypes = {
        ...alertTypes,
        [value]: { ...alertTypes[value], enabled: true },
      };
    } else {
      // unchecking a checkbox
      arrayHelpers.remove(checkboxValues.indexOf(value));

      updatedAlertTypes = {
        ...alertTypes,
        [value]: { ...alertTypes[value], enabled: false },
      };
    }
    updateAlertToastsSettings(updatedAlertTypes);
  };

  const initialValues = {
    usersAlertTypes: getCheckboxValues(alertTypes),
  };

  return (
    <div className={styles.card}>
      <div>
        <div className={styles.cardTitle}>Digital Alert Banners</div>
        <div className={styles.cardDescription}>
          Notify your agents with a banner when new alerts appear in the queue.
        </div>
        <Disclaimer availableAlertTypes={availableAlertTypes} />
      </div>
      {isFetchingAlertTypes ? (
        <Loader text="Fetching.." />
      ) : fetchAlertTypesError ? (
        <FetchErrorComponent handleFetchAlertTypes={handleFetchAlertTypes} />
      ) : (
        <div>
          {availableAlertTypes.length === 0 ? (
            <EmptyState />
          ) : (
            <Formik initialValues={initialValues}>
              {({ values, setValues }) => (
                <Form>
                  <FieldArray name="usersAlertTypes">
                    {arrayHelpers => (
                      <div
                        id="usersAlertTypes-checkbox-group"
                        aria-labelledby="usersAlertTypes-checkbox-group"
                        role="group"
                      >
                        <div className={styles.toggleBlock}>
                          <span>Notification banners</span>
                          <Switch
                            dataName="alert-toast-toggle"
                            onOffLabel
                            onClick={() => toggleAlertToasts(setValues)}
                            isOn={localAlertToastToggle}
                          />
                        </div>
                        {availableAlertTypes.map(a => (
                          <CheckBox
                            key={a.name}
                            label={a?.display_name}
                            name={a.name}
                            value={a.name}
                            isChecked={
                              !!values.usersAlertTypes.find(
                                type => type === a.name,
                              )
                            }
                            onChange={e =>
                              handleCheckbox(
                                e,
                                a.name,
                                values.usersAlertTypes,
                                arrayHelpers,
                              )
                            }
                            disabled={
                              !localAlertToastToggle ||
                              getIsCheckboxDisabled(a?.name)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </FieldArray>
                </Form>
              )}
            </Formik>
          )}
        </div>
      )}
    </div>
  );
};

export default DigitalAlertsBannerSettings;
